import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Nav } from "../components/Nav";
import { useLang } from "../i18n";
import { AREA_META } from "../constants/areaMeta";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Check, Sparkles, CalendarClock, AlertTriangle, ChevronRight, Download, RefreshCw } from "lucide-react";
import { WorkoutCard } from "../components/WorkoutCard";
import { BreathTimer } from "../components/BreathTimer";
import { parseBreathPattern } from "../constants/breath";
import jsPDF from "jspdf";
import { AiDisclaimer } from "../components/AiDisclaimer";

export default function Program() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activeWeek, setActiveWeek] = useState(0);
  const [view, setView] = useState("goals"); // goals | daily
  const [warn, setWarn] = useState(null); // {taskId, fromDate}
  const pollRef = useRef(null);

  const load = async () => {
    const r = await api.get("/program");
    setData(r.data);
    return r.data;
  };
  useEffect(() => {
    load();
    return () => clearTimeout(pollRef.current);
  }, []);

  // pick current week based on today
  useEffect(() => {
    if (data && data.weeks && data.weeks.length) {
      const today = new Date().toISOString().slice(0, 10);
      let idx = 0;
      data.weeks.forEach((w, i) => {
        if (w.days.some((d) => d.date <= today)) idx = i;
      });
      setActiveWeek(idx);
    }
  }, [data && data.status]);

  // while a regeneration is running in the background, poll to pick up the new future weeks
  useEffect(() => {
    if (!data || !data.regenerating) return;
    const id = setInterval(() => { load(); }, 15000);
    return () => clearInterval(id);
  }, [data && data.regenerating]);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/program/generate?lang=${lang}`);
      toast.info(t("program_generating"));
      const poll = async () => {
        const d = await load();
        if (d.status === "done") { setBusy(false); toast.success(lang === "it" ? "Programma pronto!" : "Program ready!"); return; }
        if (d.status === "error") { setBusy(false); toast.error("Errore"); return; }
        pollRef.current = setTimeout(poll, 5000);
      };
      pollRef.current = setTimeout(poll, 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
      setBusy(false);
    }
  };

  const toggleTask = async (taskId, done) => {
    const res = await api.patch("/program/task", { task_id: taskId, done });
    setData((d) => ({ ...d, ...res.data.program }));
  };

  const moveTask = async (taskId, fromDate, newDate) => {
    if (newDate === fromDate) return;
    const res = await api.patch("/program/task", { task_id: taskId, new_date: newDate });
    setData((d) => ({ ...d, ...res.data.program }));
    if (res.data.warning) setWarn({ taskId, fromDate });
  };

  const undoMove = async () => {
    if (!warn) return;
    await api.patch("/program/task", { task_id: warn.taskId, new_date: warn.fromDate });
    setWarn(null);
    load();
  };

  if (!data) return <Shell><div className="py-20 text-center text-[#6E6B65]">...</div></Shell>;

  // states before program exists
  if (data.status === "none" || data.status === "pending" || busy) {
    return (
      <Shell>
        <Header t={t} navigate={navigate} />
        {data.status === "pending" || busy ? (
          <Card testid="program-generating">
            <div className="flex items-center gap-3 text-[#6E6B65]">
              <span className="w-5 h-5 rounded-full border-2 border-[#3F6E85] border-t-transparent animate-spin" />
              {t("program_generating")}
            </div>
          </Card>
        ) : data.analysis_complete ? (
          <Card testid="program-generate-cta">
            <Sparkles className="w-8 h-8 text-[#B5651D] mb-3" />
            <p className="text-[#3a3a3a] mb-5">{lang === "it" ? "La tua settimana di analisi è completa. Genera ora il tuo Viaggio della Longevità di 12 settimane." : "Your analysis week is complete. Generate your 12-week Longevity Journey now."}</p>
            <Button data-testid="generate-program-btn" onClick={generate} className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59]">{t("generate_program")}</Button>
          </Card>
        ) : (
          <Card testid="program-analysis-phase">
            <CalendarClock className="w-8 h-8 text-[#3F6E85] mb-3" />
            <p className="font-serif text-2xl mb-1">{t("day_num")} {data.day}/7</p>
            <p className="text-[#3a3a3a]">{t("program_analysis_phase")}</p>
          </Card>
        )}
      </Shell>
    );
  }

  // program done
  const week = data.weeks[activeWeek] || data.weeks[0];
  const weekDone = week.days.reduce((a, d) => a + d.tasks.filter((x) => x.done).length, 0);
  const weekTotal = week.days.reduce((a, d) => a + d.tasks.length, 0);

  const downloadWeekPdf = () => {
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 44;
    const maxW = pageW - margin * 2;
    let y = 54;
    const ensure = (h) => { if (y + h > pageH - margin) { pdf.addPage(); y = margin; } };

    pdf.setFont("helvetica", "bold"); pdf.setFontSize(20); pdf.setTextColor(63, 110, 133);
    pdf.text("RISE your LIFE", margin, y); y += 20;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(12); pdf.setTextColor(110, 107, 101);
    pdf.text(`${t("week")} ${activeWeek + 1} — ${week.title}`, margin, y); y += 8;
    pdf.setDrawColor(63, 110, 133); pdf.line(margin, y, pageW - margin, y); y += 18;
    const dayNames = lang === "it" ? ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"] : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    week.days.forEach((d, di) => {
      ensure(30);
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(43, 74, 89);
      pdf.text(`${dayNames[d.weekday_index ?? di]} · ${d.date}`, margin, y); y += 16;
      d.tasks.forEach((tk) => {
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); pdf.setTextColor(40, 40, 40);
        pdf.splitTextToSize(`• ${t(tk.pillar)}: ${tk.title}`, maxW).forEach((ln) => { ensure(15); pdf.text(ln, margin + 6, y); y += 14; });
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(110, 107, 101);
        pdf.splitTextToSize(tk.detail || "", maxW - 12).forEach((ln) => { ensure(13); pdf.text(ln, margin + 14, y); y += 12; });
        if (tk.workout) {
          const wk = tk.workout;
          if (wk.warmup && wk.warmup.length) {
            pdf.setTextColor(224, 138, 60);
            pdf.splitTextToSize(`${t("workout_warmup")}: ${wk.warmup.join(", ")}`, maxW - 14).forEach((ln) => { ensure(12); pdf.text(ln, margin + 14, y); y += 12; });
          }
          pdf.setTextColor(63, 110, 133);
          (wk.exercises || []).forEach((ex) => {
            const line = `- ${ex.name}  |  ${t("workout_series")} ${ex.sets ?? "-"}  ${t("workout_reps")} ${ex.reps ?? "-"}  ${t("workout_rest")} ${ex.rest ?? "-"}`;
            pdf.splitTextToSize(line, maxW - 14).forEach((ln) => { ensure(12); pdf.text(ln, margin + 18, y); y += 12; });
          });
        }
        y += 4;
      });
      y += 8;
    });
    pdf.save(`RISE-programma-settimana-${activeWeek + 1}.pdf`);
  };

  return (
    <Shell>
      <Header t={t} navigate={navigate} />

      {data.regenerating && (
        <div className="mb-5 flex items-center gap-3 rounded-2xl border border-[#E08A3C]/30 bg-[#FBF3E9] px-4 py-3" data-testid="program-regenerating-banner">
          <RefreshCw className="w-4 h-4 text-[#B5651D] animate-spin shrink-0" />
          <p className="text-sm text-[#8a5a1f]">{t("regen_started")}</p>
        </div>
      )}

      {/* week selector */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6" data-testid="week-selector">
        {data.weeks.map((w, i) => {
          const done = w.days.reduce((a, d) => a + d.tasks.filter((x) => x.done).length, 0);
          const total = w.days.reduce((a, d) => a + d.tasks.length, 0);
          const complete = total > 0 && done === total;
          return (
            <button
              key={i}
              data-testid={`week-tab-${i + 1}`}
              onClick={() => setActiveWeek(i)}
              className={`shrink-0 w-14 h-14 rounded-2xl flex flex-col items-center justify-center text-sm font-semibold transition-all ${
                i === activeWeek ? "bg-[#3F6E85] text-white" : complete ? "bg-[#EAF0EC] text-[#3F6E85]" : "bg-white border border-stone-200 text-[#6E6B65]"
              }`}
            >
              <span className="text-[10px] uppercase opacity-70">{t("week")}</span>
              {i + 1}
            </button>
          );
        })}
      </div>

      {/* view toggle */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-serif text-3xl tracking-tight">{week.title}</h2>
          <p className="text-sm text-[#6E6B65]">{weekDone}/{weekTotal} {t("tasks_done")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={downloadWeekPdf} className="rounded-full gap-2" data-testid="download-week-pdf-btn">
            <Download className="w-4 h-4" /> {t("weekly_program_pdf")}
          </Button>
          <div className="flex rounded-full border border-stone-200 overflow-hidden text-sm">
            {[["goals", t("view_goals")], ["daily", t("view_daily")]].map(([k, label]) => (
              <button key={k} data-testid={`view-${k}`} onClick={() => setView(k)}
                className={`px-4 py-2 transition-colors ${view === k ? "bg-[#3F6E85] text-white" : "text-[#6E6B65]"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {view === "goals" ? (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl p-8 bg-white border border-stone-200" data-testid="week-goals">
          <p className="text-[#3a3a3a] mb-6 text-lg">{week.focus}</p>
          <div className="flex flex-wrap gap-2 mb-6">
            {(week.pillars_emphasis || []).map((p) => {
              const meta = AREA_META[p];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <span key={p} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium" style={{ backgroundColor: meta.tint, color: meta.color }}>
                  <Icon className="w-4 h-4" /> {t(p)}
                </span>
              );
            })}
          </div>
          <Button variant="outline" onClick={() => setView("daily")} className="rounded-full gap-2" data-testid="goto-daily">
            {t("view_daily")} <ChevronRight className="w-4 h-4" />
          </Button>
        </motion.div>
      ) : (
        <div className="space-y-4" data-testid="week-daily">
          {week.days.map((day) => (
            <div key={day.date} className="rounded-2xl bg-white border border-stone-200 p-5" data-testid={`day-${day.date}`}>
              <p className="text-xs uppercase tracking-widest text-[#6E6B65] mb-3">
                {new Date(day.date + "T00:00:00").toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { weekday: "long", day: "numeric", month: "short" })}
              </p>
              <div className="space-y-3">
                {day.tasks.map((tk) => {
                  const meta = AREA_META[tk.pillar] || {};
                  const Icon = meta.icon;
                  return (
                    <div key={tk.id} className="flex items-start gap-3" data-testid={`task-${tk.id}`}>
                      <button
                        onClick={() => toggleTask(tk.id, !tk.done)}
                        data-testid={`task-toggle-${tk.id}`}
                        className={`mt-0.5 w-6 h-6 rounded-lg shrink-0 flex items-center justify-center border-2 transition-colors ${tk.done ? "bg-[#3F6E85] border-[#3F6E85]" : "border-stone-300"}`}
                      >
                        {tk.done && <Check className="w-4 h-4 text-white" />}
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {Icon && <Icon className="w-4 h-4" style={{ color: meta.color }} />}
                          <span className={`font-medium ${tk.done ? "line-through text-[#6E6B65]" : "text-[#2B4A59]"}`}>{tk.title}</span>
                        </div>
                        <p className="text-sm text-[#6E6B65] mt-0.5">{tk.detail}</p>
                        {tk.workout && <WorkoutCard workout={tk.workout} />}
                        {tk.pillar === "breathing" && (
                          <div className="mt-3 rounded-2xl border border-[#86A6B8]/25 bg-[#EEF3F6] px-3 py-2" data-testid={`breath-task-${tk.id}`}>
                            <BreathTimer color="#86A6B8" compact pattern={parseBreathPattern(`${tk.title} ${tk.detail || ""}`)} />
                          </div>
                        )}
                      </div>
                      <select
                        data-testid={`task-move-${tk.id}`}
                        value={day.date}
                        onChange={(e) => moveTask(tk.id, day.date, e.target.value)}
                        title={t("move_to")}
                        className="text-xs rounded-lg border border-stone-200 px-2 py-1 bg-white text-[#6E6B65]"
                      >
                        {week.days.map((d2) => (
                          <option key={d2.date} value={d2.date}>
                            {new Date(d2.date + "T00:00:00").toLocaleDateString(lang === "it" ? "it-IT" : "en-US", { weekday: "short", day: "numeric" })}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
                {day.tasks.length === 0 && <p className="text-sm text-[#6E6B65]">—</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <AiDisclaimer />

      {/* balance warning modal */}
      {warn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="balance-warning">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="flex items-center gap-3 mb-3 text-[#B7410E]">
              <AlertTriangle className="w-6 h-6" />
              <h3 className="font-serif text-2xl">{t("balance_warning_title")}</h3>
            </div>
            <p className="text-[#3a3a3a] mb-6">{t("balance_warning_body")}</p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={undoMove} className="rounded-full" data-testid="warning-undo">{t("cancel")}</Button>
              <Button onClick={() => setWarn(null)} className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59]" data-testid="warning-proceed">{t("proceed")}</Button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="App min-h-screen">
    <Nav />
    <div className="max-w-4xl mx-auto px-6 py-10">{children}</div>
  </div>
);

const Header = ({ t, navigate }) => (
  <>
    <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-[#6E6B65] mb-4 hover:text-[#3F6E85]" data-testid="program-back">
      <ArrowLeft className="w-4 h-4" /> {t("back")}
    </button>
    <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-2">{t("journey")}</h1>
    <p className="text-[#6E6B65] mb-8 max-w-xl">{t("journey_sub")}</p>
  </>
);

const Card = ({ children, testid }) => (
  <div className="rounded-3xl p-8 bg-[#F3F1ED] border border-stone-200" data-testid={testid}>{children}</div>
);
